import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Module,
  Course,
  CourseProgress,
  ModuleProgress,
  SelectedVideo,
} from "../types";
import {
  fetchModules,
  fetchModuleCourses,
  fetchModuleProgressions,
  fetchUserProgress,
  updateCourseProgress,
  fetchCourseProgress,
  markModuleAsCompleted,
  fetchModuleAccessibility,
} from "../api";

// Définition de types manquants
type UserId = string;

// Hook pour la gestion d'authentification (à extraire dans un hook séparé)
const useAuth = (): { userId: UserId } => {
  return {
    userId: (localStorage.getItem("userId") as UserId) || "",
  };
};

// Hook pour les modules - Augmenté du staleTime plus long
const useModules = () => {
  const {
    data: modules = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["modules"],
    queryFn: fetchModules,
    staleTime: 5 * 60 * 1000, // 5 minutes - données relativement statiques
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
  return { modules, isLoading, error };
};

// Hook pour la progression des modules - Augmenté du staleTime approprié
const useModuleProgressions = () => {
  const { data: moduleProgressData = [], isLoading } = useQuery({
    queryKey: ["moduleProgressions"],
    queryFn: fetchModuleProgressions,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
  return { moduleProgressData, isLoading };
};

// Hook pour l'accessibilité des modules (récupère accessibleModules et completedModules de l'API)
const useModuleAccessibility = () => {
  const { data: moduleAccessibility = { accessibleModules: [], completedModules: [] }, isLoading } = useQuery({
    queryKey: ["moduleAccessibility"],
    queryFn: fetchModuleAccessibility,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
  return { moduleAccessibility, isLoading };
};

// Hook pour la progression utilisateur - Corrigé les problèmes de gestion d'erreur
const useUserProgress = () => {
  const { userId } = useAuth();

  const {
    data: progressData = [],
    isLoading,
    refetch,
    error: queryError,
  } = useQuery({
    queryKey: ["userProgress", userId], // Ajout de l'userId comme dépendance
    queryFn: async () => {
      try {
        const data = await fetchUserProgress();
        return data;
      } catch (e) {
        // En mode développement, retourner des données fictives en cas d'erreur
        if (import.meta.env.DEV) {
          console.warn("Utilisation de données fictives en mode développement");
          return [];
        }
        throw e; // En production, propager l'erreur pour une meilleure gestion
      }
    },
    retry: 1, // Réduire le nombre de tentatives
    retryDelay: 1000,
    staleTime: 30000, // 30 secondes
    gcTime: 5 * 60 * 1000, // 5 minutes - Plus approprié
    enabled: !!userId, // Ne pas exécuter si pas d'userId
  });

  return {
    progressData,
    isLoading,
    refetch,
    error: queryError,
  };
};

// Hook principal qui utilise les hooks précédents - OPTIMISÉ
export const useCineProState = () => {
  const queryClient = useQueryClient();
  const { userId } = useAuth();

  // Utilisation de hooks avec leur nouveau comportement
  const {
    modules,
    isLoading: isLoadingModules,
    error: modulesError,
  } = useModules();

  const { moduleProgressData, isLoading: isLoadingModuleProgress } =
    useModuleProgressions();

  const { moduleAccessibility, isLoading: isLoadingModuleAccessibility } =
    useModuleAccessibility();

  const {
    progressData,
    isLoading: isLoadingProgress,
    refetch: refetchProgress,
    error: userProgressError,
  } = useUserProgress();

  // États UI locaux
  const [selectedVideo, setSelectedVideo] = useState<SelectedVideo | null>(
    null
  );
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState<Date>(new Date());

  // Mutation pour mettre à jour la progression d'un cours - OPTIMISÉE
  const updateProgressMutation = useMutation({
    mutationFn: ({
      courseId,
      progress,
      completed,
    }: {
      courseId: string;
      progress: number;
      completed: boolean;
    }) => updateCourseProgress(courseId, progress, completed),
    onSuccess: () => {
      // Invalidation ciblée seulement
      queryClient.invalidateQueries({ queryKey: ["userProgress", userId] });
      setLastSync(new Date());
    },
    onError: () => {
      toast.error(
        "Un problème est survenu lors de la sauvegarde de votre progression. Veuillez réessayer."
      );
    },
  });

  // Mutation pour marquer un module comme complété - OPTIMISÉE
  const completeModuleMutation = useMutation({
    mutationFn: (moduleId: string) => markModuleAsCompleted(moduleId),
    onSuccess: () => {
      // Invalidation ciblée seulement
      queryClient.invalidateQueries({ queryKey: ["moduleProgressions"] });
    },
    onError: () => {
      toast.error(
        "Impossible de marquer ce module comme terminé. Veuillez réessayer."
      );
    },
  });

  // Effet pour sélectionner le premier module actif au chargement - INCHANGÉ
  useEffect(() => {
    if (modules.length > 0 && !selectedModuleId) {
      const firstEnabledModule = modules.find((m: Module) => m.isEnabled);
      if (firstEnabledModule) {
        setSelectedModuleId(firstEnabledModule.id);
      }
    }
  }, [modules, selectedModuleId]);

  // CORRECTION IMPORTANTE: Mémorisation des états dérivés

  // Fonction pour déterminer si un cours est verrouillé (mémorisée)
  const isCourseUnlocked = useCallback(
    (moduleId: string, courseOrder: number): boolean => {
      // Si le module n'est pas activé, tous ses cours sont verrouillés
      const moduleEnabled = modules.find(
        (m: Module) => m.id === moduleId
      )?.isEnabled;
      if (moduleEnabled === false) return false;

      // Le premier cours du premier module est toujours déverrouillé
      const isFirstModule = modules.length > 0 && modules[0].id === moduleId;
      if (isFirstModule && courseOrder === 1) return true;

      // Pour les autres cours, vérifier si les cours précédents sont complétés
      const module = modules.find((m: Module) => m.id === moduleId);
      if (!module || !module.courses) return false;

      // Pour le premier cours des autres modules, vérifier si le module précédent est complété
      if (courseOrder === 1 && !isFirstModule) {
        const moduleIndex = modules.findIndex((m: Module) => m.id === moduleId);
        if (moduleIndex <= 0) return false;

        const prevModule = modules[moduleIndex - 1];
        if (!prevModule) return false;

        // Vérifier si le module précédent est complété
        const moduleCompleted = moduleProgressData.some(
          (progress: ModuleProgress) =>
            progress.moduleId === prevModule.id && progress.completed
        );

        return moduleCompleted;
      }

      // Vérifier si tous les cours précédents sont complétés
      const previousCourses = module.courses.filter(
        (c: Course) => c.order < courseOrder
      );
      return previousCourses.every((course: Course) => {
        // Vérifier la progression du cours spécifiquement
        const courseCompleted = progressData.some(
          (progress: CourseProgress) =>
            progress.courseId === course.id && progress.completed
        );

        return courseCompleted;
      });
    },
    [modules, progressData, moduleProgressData]
  );

  // CORRECTION CRITIQUE: Utilisation de useMemo pour éviter les calculs excessifs
  const updatedModules = useMemo(() => {
    return modules.map((module: Module) => {
      // Déterminer si le module est accessible en utilisant les données de l'API
      const isModuleAccessible = 
        moduleAccessibility.accessibleModules.length > 0 
          ? moduleAccessibility.accessibleModules.includes(module.id)
          : module.isEnabled !== false; // Fallback sur la valeur du module si pas de données API

      if (!module.courses) {
        return {
          ...module,
          isEnabled: isModuleAccessible,
        };
      }

      const updatedCourses = module.courses.map((course: Course) => {
        const unlocked = isCourseUnlocked(module.id, course.order);
        const completed = progressData.some(
          (progress: CourseProgress) =>
            progress.courseId === course.id && progress.completed
        );

        return {
          ...course,
          locked: !unlocked,
          completed,
        };
      });

      return {
        ...module,
        isEnabled: isModuleAccessible,
        courses: updatedCourses,
      };
    });
  }, [modules, progressData, isCourseUnlocked, moduleAccessibility]);

  // Fonction pour rafraîchir manuellement les données - OPTIMISÉE
  const handleRefresh = useCallback(async (): Promise<void> => {
    if (isRefreshing) return;

    try {
      setIsRefreshing(true);
      // Utilisation d'un timeout pour éviter le blocage de l'UI
      await Promise.all([
        refetchProgress(),
        queryClient.invalidateQueries({ queryKey: ["moduleProgressions"] }),
      ]);
      setLastSync(new Date());
    } catch (error) {
      console.error("Erreur lors du rafraîchissement:", error);
      toast.error("Erreur lors du rafraîchissement des données");
    } finally {
      // Garantir que le state isRefreshing est toujours remis à false
      setTimeout(() => setIsRefreshing(false), 300);
    }
  }, [isRefreshing, refetchProgress, queryClient]);

  // Fonction pour mettre à jour la progression d'un cours - OPTIMISÉE
  const handleUpdateCourseProgress = useCallback(
    async (
      courseId: string,
      progress: number,
      completed: boolean = false
    ): Promise<void> => {
      // Mise à jour optimiste pour l'UI avant l'appel à l'API
      queryClient.setQueryData(
        ["userProgress", userId],
        (oldData: CourseProgress[] = []) => {
          const existingIndex = oldData.findIndex(
            (p: CourseProgress) => p.courseId === courseId
          );
          if (existingIndex >= 0) {
            const updated = [...oldData];
            updated[existingIndex] = {
              ...updated[existingIndex],
              progression: progress,
              completed,
              lastWatchedAt: new Date().toISOString(),
            };
            return updated;
          } else {
            return [
              ...oldData,
              {
                id: Math.random().toString(36).slice(2, 9),
                userId,
                courseId,
                progression: progress,
                completed,
                lastWatchedAt: new Date().toISOString(),
              },
            ];
          }
        }
      );

      // Appel à l'API ensuite
      updateProgressMutation.mutate({ courseId, progress, completed });

      // Si le cours est complété, vérifier si le module est complété
      if (completed) {
        const module = modules.find((m: Module) =>
          m.courses?.some((c: Course) => c.id === courseId)
        );
        if (module) {
          const allCoursesInModule = module.courses || [];
          const isLastCourse =
            allCoursesInModule.findIndex((c: Course) => c.id === courseId) ===
            allCoursesInModule.length - 1;

          if (isLastCourse) {
            const allCoursesCompleted = allCoursesInModule.every(
              (c: Course) =>
                c.id === courseId ||
                progressData.some(
                  (p: CourseProgress) => p.courseId === c.id && p.completed
                )
            );

            if (allCoursesCompleted) {
              completeModuleMutation.mutate(module.id);
            }
          }
        }
      }
    },
    [
      userId,
      queryClient,
      updateProgressMutation,
      modules,
      progressData,
      completeModuleMutation,
    ]
  );

  // Fonction pour marquer un module comme complété - OPTIMISÉE
  const handleMarkModuleAsCompleted = useCallback(
    async (moduleId: string): Promise<void> => {
      completeModuleMutation.mutate(moduleId);
    },
    [completeModuleMutation]
  );

  // Fonction pour gérer le clic sur un module - OPTIMISÉE
  const handleModuleClick = useCallback(
    (moduleId: string): void => {
      if (selectedModuleId === moduleId) return; // Éviter les clics répétés

      setSelectedModuleId(moduleId);

      // Précharger les cours du module sélectionné
      const module = modules.find((m: Module) => m.id === moduleId);

      // Si le module a déjà des cours chargés, ne pas faire de requête inutile
      if (module && (!module.courses || module.courses.length === 0)) {
        queryClient.prefetchQuery({
          queryKey: ["moduleCourses", moduleId],
          queryFn: () => fetchModuleCourses(moduleId),
          staleTime: 60000, // 1 minute
          gcTime: 5 * 60 * 1000, // 5 minutes
        });
      }
    },
    [modules, queryClient, selectedModuleId]
  );

  // Fonction pour ouvrir la vidéo - OPTIMISÉE
  const openVideo = useCallback(
    async (moduleTitle: string, course: Course): Promise<void> => {
      // Vérifier si le cours est verrouillé
      if (course.locked) {
        toast.warning(
          "Veuillez compléter les capsules précédentes pour débloquer celle-ci."
        );
        return;
      }

      try {
        // Vérifier que le cours a une URL vidéo
        if (!course.videoUrl) {
          console.warn(`Le cours ${course.id} n'a pas d'URL vidéo définie`);
        }

        // Définir la vidéo sélectionnée d'abord pour une UX plus réactive
        setSelectedVideo({
          title: course.title,
          module: moduleTitle,
          courseId: course.id,
          order: course.order,
          videoUrl: course.videoUrl, // Inclure l'URL vidéo du cours dans le payload
        });

        // Récupérer la progression en arrière-plan
        const courseProgress = await fetchCourseProgress(course.id);

        // Mettre à jour le cache React Query avec la progression du cours
        if (courseProgress) {
          queryClient.setQueryData(
            ["userProgress", userId],
            (oldData: CourseProgress[] = []) => {
              const existingIndex = oldData.findIndex(
                (p: CourseProgress) => p.courseId === course.id
              );
              if (existingIndex >= 0) {
                const updated = [...oldData];
                updated[existingIndex] = {
                  ...updated[existingIndex],
                  ...courseProgress,
                };
                return updated;
              } else {
                return [...oldData, courseProgress];
              }
            }
          );
        }
      } catch (error) {
        console.error("Erreur lors de l'ouverture de la vidéo:", error);
        toast.error(
          "Impossible d'ouvrir cette capsule. Veuillez réessayer plus tard."
        );
      }
    },
    [queryClient, userId]
  );

  // Fonction pour fermer la vidéo - OPTIMISÉE
  const closeVideo = useCallback((): void => {
    setSelectedVideo(null);
    // Rafraîchir uniquement après une seconde pour éviter les effets visuels désagréables
    setTimeout(() => handleRefresh(), 1000);
  }, [handleRefresh]);

  // Indication de chargement global - OPTIMISÉE avec un délai minimum
  const loading = useMemo(
    () => isLoadingModules || isLoadingProgress || isLoadingModuleProgress || isLoadingModuleAccessibility,
    [isLoadingModules, isLoadingProgress, isLoadingModuleProgress, isLoadingModuleAccessibility]
  );

  // Gestion des erreurs - AMÉLIORÉE
  const error = useMemo(() => {
    if (modulesError) return modulesError;
    if (userProgressError) return userProgressError;
    return null;
  }, [modulesError, userProgressError]);

  return {
    modules: updatedModules,
    loading,
    error,
    selectedModuleId,
    progressData,
    moduleProgressData,
    isRefreshing,
    lastSync,
    selectedVideo,
    handleRefresh,
    handleUpdateCourseProgress,
    handleModuleClick,
    handleMarkModuleAsCompleted,
    openVideo,
    closeVideo,
  };
};
